import {
  CheckCircle,
  Refresh,
  ErrorOutline,
  Devices,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  IconButton,
  Alert,
  Avatar,
  ListItemAvatar,
} from '@mui/material'
import React, { useCallback, useMemo, useReducer } from 'react'
import { useTranslation } from 'react-i18next'

import { setDurationTestStatus } from '@/api/Device'
import { Status, DurationTestStatus } from '@/api/definition'
import { layoutStyles } from '@/utils/theme'
import useResponseValidator from '@/pages/utils/responseValidator'
import Concerns from '@/pages/Concerns'

import {
  initialDevicesInTestState,
  devicesInTestReducer,
  DevicesInTestContext,
} from './DevicesInTestContext'
import SelectDevicesToStopTestStep from './SelectDevicesToStopTestStep'

// Request Status Indicator Component
const RequestStatusIndicator: React.FC<{
  status: number | string
  children?: React.ReactNode
}> = ({ status, children }) => {
  const getNormalizedStatus = (status: number | string): number => {
    if (typeof status === 'string') {
      if (status === 'loading') return Status.IN_PROGRESS
      if (status === 'success') return Status.SUCCESS
      return Status.FAILURE
    }
    return status
  }

  const normalizedStatus = getNormalizedStatus(status)

  switch (normalizedStatus) {
    case Status.IN_PROGRESS:
      return <CircularProgress size={24} color="primary" />
    case Status.SUCCESS:
      return <CheckCircle color="success" />
    case Status.FAILURE:
      return <>{children || <ErrorOutline color="error" />}</>
    default:
      return null
  }
}

const DurationTestInProgressStep: React.FC = () => {
  const { t } = useTranslation(['app'])
  const { showTransferError } = useResponseValidator()
  const [devicesInTestState, devicesInTestDispatch] = useReducer(
    devicesInTestReducer,
    initialDevicesInTestState,
  )

  const handleStopDurationTest = useCallback(async () => {
    if (devicesInTestState.selectedDevices.length === 0) {
      devicesInTestDispatch({
        type: 'SET_ERROR',
        payload: 'No devices selected',
      })
      return
    }

    devicesInTestDispatch({ type: 'SET_LOADING', payload: true })
    devicesInTestDispatch({ type: 'SET_ERROR', payload: null })

    try {
      devicesInTestDispatch({ type: 'SET_ACTIVE_STEP', payload: 2 })

      for (const device of devicesInTestState.selectedDevices) {
        devicesInTestDispatch({
          type: 'SET_REQUEST_STATUS',
          payload: { uid: device.uid, status: { status: 'loading' as const } },
        })

        const results = await setDurationTestStatus(
          [device.uid],
          DurationTestStatus.DurationTestCanceled,
        )
        const result = results[0]

        if (result.status === Status.SUCCESS) {
          devicesInTestDispatch({
            type: 'SET_REQUEST_STATUS',
            payload: {
              uid: device.uid,
              status: { status: 'success' as const },
            },
          })
        } else {
          // Show HTTP error in snackbar
          showTransferError(result)

          devicesInTestDispatch({
            type: 'SET_REQUEST_STATUS',
            payload: {
              uid: device.uid,
              status: {
                status: 'error' as const,
                message: result.reason || 'Failed to stop duration test',
              },
            },
          })
        }
      }
    } catch (error) {
      devicesInTestDispatch({
        type: 'SET_ERROR',
        payload:
          error instanceof Error
            ? error.message
            : 'Failed to stop duration test',
      })
    } finally {
      devicesInTestDispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [devicesInTestState.selectedDevices, showTransferError])

  const handleBackToStep1 = useCallback(() => {
    devicesInTestDispatch({ type: 'SET_ACTIVE_STEP', payload: 1 })
  }, [])

  const handleRetry = useCallback(
    async (uid: string) => {
      devicesInTestDispatch({
        type: 'SET_REQUEST_STATUS',
        payload: { uid, status: { status: 'loading' as const } },
      })

      const results = await setDurationTestStatus(
        [uid],
        DurationTestStatus.DurationTestCanceled,
      )
      const result = results[0]

      if (result.status === Status.SUCCESS) {
        devicesInTestDispatch({
          type: 'SET_REQUEST_STATUS',
          payload: { uid, status: { status: 'success' as const } },
        })
      } else {
        // Show HTTP error in snackbar
        showTransferError(result)

        devicesInTestDispatch({
          type: 'SET_REQUEST_STATUS',
          payload: {
            uid,
            status: {
              status: 'error' as const,
              message: result.reason || 'Failed to stop duration test',
            },
          },
        })
      }
    },
    [showTransferError],
  )

  const devicesInTestContextValue = useMemo(
    () => ({
      state: devicesInTestState,
      dispatch: devicesInTestDispatch,
      onStopDurationTest: handleStopDurationTest,
      onBackToStep1: handleBackToStep1,
    }),
    [
      devicesInTestState,
      devicesInTestDispatch,
      handleStopDurationTest,
      handleBackToStep1,
    ],
  )

  const renderStep1 = () => (
    <>
      <SelectDevicesToStopTestStep />
      {devicesInTestState.error && (
        <Typography color="error" sx={{ mt: 2 }}>
          {devicesInTestState.error}
        </Typography>
      )}
    </>
  )

  const renderStep2 = () => (
    <Box>
      <List
        sx={{
          width: '100%',
          backgroundColor: 'background.paper',
          borderRadius: 1,
          boxShadow: 1,
        }}
      >
        {devicesInTestState.selectedDevices.map((device) => {
          const request = devicesInTestState.requestStatuses.get(device.uid)
          return (
            <React.Fragment key={device.uid}>
              <ListItem
                alignItems="flex-start"
                sx={{
                  pr: 8,
                  position: 'relative',
                  borderBottom: '1px solid #eee',
                }}
              >
                <ListItemAvatar>
                  <Avatar>
                    <Devices />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={device.serial ?? device.uid}
                  secondary={`${device.type ?? 'Unknown Type'}${device.label ? ' - ' + device.label : ''}`}
                />
                {request && (
                  <Box
                    sx={{
                      position: 'absolute',
                      right: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <RequestStatusIndicator status={request.status}>
                      {request.status === 'error' && (
                        <IconButton
                          size="small"
                          onClick={() => void handleRetry(device.uid)}
                          color="error"
                          aria-label={t('common:general.retry')}
                          sx={{
                            minWidth: 0,
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            p: 0,
                            backgroundColor: 'takeoutofservice.main',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'takeoutofservice.dark',
                            },
                          }}
                        >
                          <Refresh fontSize="small" />
                        </IconButton>
                      )}
                    </RequestStatusIndicator>
                  </Box>
                )}
              </ListItem>
              {/* Add Concerns component after each ListItem */}
              {request?.status === 'error' && request.message && (
                <Concerns message={t(request.message)} concerns={[]} />
              )}
            </React.Fragment>
          )
        })}
      </List>

      {devicesInTestState.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {devicesInTestState.error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 3 }}>
        <Button
          variant="contained"
          onClick={handleBackToStep1}
          sx={{
            marginTop: 2,
            marginRight: 2,
            color: '#fff',
          }}
          disabled={devicesInTestState.isLoading}
        >
          {t('common:general.back')}
        </Button>
      </Box>
    </Box>
  )

  const steps = [
    t('device.durationTest.selectDevicesToStopTest'),
    t('device.durationTest.stopDurationTest'),
  ]

  return (
    <DevicesInTestContext.Provider value={devicesInTestContextValue}>
      <Box sx={{ ...layoutStyles.pageContainer, mt: 0, p: 3 }}>
        <Stepper
          activeStep={devicesInTestState.activeStep - 1}
          alternativeLabel
          sx={{ mb: 4 }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Box
          data-testid="box-1"
          sx={{ mt: 4, position: 'relative', overflow: 'hidden' }}
        >
          <Box
            sx={{
              transform: `translateX(-${(devicesInTestState.activeStep - 1) * 100}%)`,
              transition: 'transform 0.3s ease-in-out',
              display: 'flex',
            }}
          >
            <Box sx={{ width: '100%', flexShrink: 0 }}>{renderStep1()}</Box>
            <Box sx={{ width: '100%', flexShrink: 0 }}>{renderStep2()}</Box>
          </Box>
        </Box>
      </Box>
    </DevicesInTestContext.Provider>
  )
}

export default DurationTestInProgressStep
